import type { ConciseBranch } from '@festina/configuration-general-types/configuration'
import { type BranchKey, STANDARD_BRANCH } from '@festina-life/api'
import { notEmpty, type Optional, typeUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { Alert, type Severity } from '@festina-life/ui/feedback'
import {
  FormikCheckbox,
  FormikForm,
  FormikSelect,
  FormikTextInput,
} from '@festina-life/ui/formikForms'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import { Stack } from '@festina-life/ui/structure'
import { useRef } from 'react'
import { useNavigate } from 'react-router'
import * as Yup from 'yup'

import { server as serverPath } from '../../../../system/paths'
import { useNavigationParams } from '../../../../system/useNavigationParams'
import { isEqualBranch } from '../../../../uiModels/branches/helpers'
import { isProtectedBranchCategory } from '../../../../uiModels/branches/types'
import { useAllConciseBranches } from '../../../../uiModels/branches/useAllConciseBranches'
import { downloadConfigurationFile } from '../helpers'
import { type FinalUpgradeStatus, isFinalUpgradeStatus } from './types'
import { useNonAncestorBranchesOnServer } from './useNonAncestorBranchesOnServer'
import { useUpgradeBranch } from './useUpgradeBranch'

export type UpgradeBranchInput = {
  toBranch: Optional<ConciseBranch>
  upgradeBranchName: string
  shouldSquash: boolean
  openBranchAfterUpgrade: boolean
}

const validate = typeUtils.createValidator<UpgradeBranchInput>()
const finalStatusSeverity: Record<FinalUpgradeStatus, Severity> = {
  success: 'success',
  error: 'error',
  compareWarning: 'warning',
}

type Props = {
  branchKey: BranchKey
}

export const UpgradeBranchForm = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const allBranches = useAllConciseBranches()
  const upgradeBranchCandidates = useNonAncestorBranchesOnServer(branchKey)
  const { mutateAsync, status, error, configurationPackage } =
    useUpgradeBranch(branchKey)
  const currentBranchToValueRef = useRef(upgradeBranchCandidates[0])

  const navigate = useNavigate()
  const {
    server: currentServer,
    branch: currentBranch,
    block,
    layer,
  } = useNavigationParams()

  const isTargetOpen = isEqualBranch(
    branchKey,
    currentBranch ? { name: currentBranch, server: currentServer } : undefined,
  )

  const targetBranch = allBranches[branchKey.server]?.find(
    (branch) => branch.name === branchKey.name,
  )
  const isTargetProtected = isProtectedBranchCategory(
    targetBranch?.branchCategory,
  )

  if (!upgradeBranchCandidates.length) {
    return (
      <Alert
        severity="warning"
        title={translate('branchDetailsPanel.tabs.manage.upgrade.noBranches', {
          server: branchKey.server,
        })}
      />
    )
  }

  const validationSchema = Yup.object({
    upgradeBranchName: isTargetProtected
      ? Yup.string()
          .required(translate('validation.required'))
          .noSpaces(translate('validation.noSpaces'))
          .notForbidden(translate('validation.notForbidden'), [STANDARD_BRANCH])
          .notDuplicated(
            translate('validation.duplicated'),
            (allBranches?.[branchKey.server] ?? [])
              .map((conciseBranch) => conciseBranch.name)
              .filter(notEmpty),
          )
      : Yup.string().optional(),
    toBranch: Yup.object().required(translate('validation.required')),
  })

  const handleUpgradeBranch = async (values: UpgradeBranchInput) => {
    const result = await mutateAsync({
      ...values,
      shouldForce: status === 'compareWarning',
    })

    if (result && !isTargetOpen && values.openBranchAfterUpgrade) {
      await navigate(
        serverPath({
          server: result.hostBranchKey.server,
          branch: result.hostBranchKey.name,
          block,
          layer,
        }),
      )
    }
  }

  const defaultUpgradeBranchNameForToBranch = (toBranchName: string) =>
    `${branchKey.name}-upgrade-to-${toBranchName}`

  const initialValues: UpgradeBranchInput = {
    toBranch: currentBranchToValueRef.current,
    upgradeBranchName: defaultUpgradeBranchNameForToBranch(
      currentBranchToValueRef.current.name ?? '',
    ),
    shouldSquash: false,
    openBranchAfterUpgrade: false,
  }
  const isUpgradeInProgress = status && !isFinalUpgradeStatus(status)
  const finalStatusTranslations: Record<FinalUpgradeStatus, string> = {
    success: translate(
      'branchDetailsPanel.tabs.manage.upgrade.configurationPackage.success',
    ),
    error: translate(
      'branchDetailsPanel.tabs.manage.upgrade.configurationPackage.error',
    ),
    compareWarning: translate(
      'branchDetailsPanel.tabs.manage.upgrade.configurationPackage.error',
    ),
  }

  return (
    <FormikForm
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleUpgradeBranch}
    >
      {({ values, setFieldValue, touched }) => {
        const defaultBranchNameForToBranch =
          defaultUpgradeBranchNameForToBranch(values.toBranch?.name ?? '')

        if (
          values.toBranch &&
          values.toBranch !== currentBranchToValueRef.current
        ) {
          currentBranchToValueRef.current = values.toBranch

          if (!touched.upgradeBranchName) {
            void setFieldValue(
              'upgradeBranchName',
              defaultBranchNameForToBranch,
              true,
            )
          }
        }

        const nonForceSubmitLabel = isTargetProtected
          ? translate(
              'branchDetailsPanel.tabs.manage.upgrade.form.submitButton',
            )
          : translate(
              'branchDetailsPanel.tabs.manage.upgrade.form.upgradeButton',
            )

        return (
          <Stack gap="display" className="flex-col max-w-md">
            {isTargetProtected && (
              <Alert
                severity="info"
                title={translate(
                  'branchDetailsPanel.tabs.manage.upgrade.form.protectedBranch.warning',
                  { targetBranchName: branchKey.name },
                )}
              />
            )}
            <FormikSelect
              label={translate(
                'branchDetailsPanel.tabs.manage.upgrade.form.toBranch.label',
              )}
              description={translate(
                'branchDetailsPanel.tabs.manage.upgrade.form.toBranch.description',
              )}
              name={validate('toBranch')}
              options={upgradeBranchCandidates}
              renderFn={({ name }) => name ?? ''}
              keyFn={({ name }) => name ?? ''}
              shouldShowClearButton={false}
            />
            {isTargetProtected ? (
              <FormikTextInput
                name={validate('upgradeBranchName')}
                label={translate(
                  'branchDetailsPanel.tabs.manage.upgrade.form.upgradeBranchName.label',
                )}
                description={translate(
                  'branchDetailsPanel.tabs.manage.upgrade.form.upgradeBranchName.description',
                )}
              />
            ) : (
              <FormikCheckbox
                name={validate('shouldSquash')}
                label={translate(
                  'branchDetailsPanel.tabs.manage.upgrade.form.squash.label',
                )}
                description={translate(
                  'branchDetailsPanel.tabs.manage.upgrade.form.squash.description',
                  { targetBranchName: branchKey.name },
                )}
              />
            )}
            {!isTargetOpen && (
              <FormikCheckbox
                name={validate('openBranchAfterUpgrade')}
                label={translate(
                  'branchDetailsPanel.tabs.manage.upgrade.form.openBranchAfterUpgrade.label',
                )}
                description={translate(
                  'branchDetailsPanel.tabs.manage.upgrade.form.openBranchAfterUpgrade.description',
                )}
              />
            )}
            <div className="flex flex-col gap-y-2">
              {error && (
                <Alert
                  severity={status === 'compareWarning' ? 'warning' : 'error'}
                  title={error}
                />
              )}
              {status && (
                <Alert
                  isLoading={!isFinalUpgradeStatus(status)}
                  severity={
                    isFinalUpgradeStatus(status)
                      ? finalStatusSeverity[status]
                      : undefined
                  }
                >
                  {status === 'success' && !isTargetProtected
                    ? translate(
                        'branchDetailsPanel.tabs.manage.upgrade.enum.status.successDirect',
                        { targetBranchName: branchKey.name },
                      )
                    : translateEnum(
                        'branchDetailsPanel.tabs.manage.upgrade.enum.status',
                        status,
                      )}
                </Alert>
              )}
              {configurationPackage &&
                status &&
                isFinalUpgradeStatus(status) && (
                  <div>
                    <Button
                      variant="discreet"
                      className="p-0! mr-1 underline"
                      onClick={() =>
                        downloadConfigurationFile(
                          configurationPackage,
                          `${values.upgradeBranchName}-${status}.json`,
                        )
                      }
                    >
                      {translate(
                        'branchDetailsPanel.tabs.manage.upgrade.configurationPackage.downloadButton',
                      )}
                    </Button>
                    <span>{finalStatusTranslations[status]}</span>
                  </div>
                )}
            </div>
            <div className="flex w-full justify-end">
              <Button
                type="submit"
                isLoading={isUpgradeInProgress}
                disabled={status === 'error'}
              >
                {status === 'compareWarning'
                  ? translate(
                      'branchDetailsPanel.tabs.manage.upgrade.form.forceSubmitButton',
                    )
                  : nonForceSubmitLabel}
              </Button>
            </div>
          </Stack>
        )
      }}
    </FormikForm>
  )
}
